/**
 * Custom hooks for hunt list operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { huntService } from '../services/huntService';

/**
 * Hook to fetch all hunts
 * @param {Object} options - Query options
 * @returns {Object} Query result with data, loading, and error states
 */
export function useHunts(options = {}) {
  const { page = 1, limit = 20, filters = {} } = options;

  return useQuery({
    queryKey: ['hunts', { page, limit, filters }],
    queryFn: () => huntService.getHunts({ page, limit, filters }),
    placeholderData: (previousData) => previousData
  });
}

/**
 * Hook to create a new hunt entry
 * @returns {Object} Mutation result with mutate function
 */
export function useCreateHunt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: huntService.createHunt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hunts'] });
    }
  });
}

/**
 * Hook to update an existing hunt entry
 * @returns {Object} Mutation result with mutate function
 */
export function useUpdateHunt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }) => huntService.updateHunt(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hunts'] });
    }
  });
}

/**
 * Hook to delete a hunt entry
 * @returns {Object} Mutation result with mutate function
 */
export function useDeleteHunt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: huntService.deleteHunt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hunts'] });
    }
  });
}
